import { orderBy } from 'lodash';

// A shift running past midnight is stored as two docs: a head closing at a
// true 11:59 PM, and a tail on the next day opening at 12:00 AM. Anything
// closing earlier is a real break before midnight and stays a separate entry.
// Must match the same window in getTimeSlots.js, or the hours listed here can
// disagree with the slots that are actually bookable.
const OVERNIGHT_HEAD_MIN_END = 86340; // 11:59 PM
const OVERNIGHT_TAIL_MAX_END = 12 * 3600; // 12:00 PM

const nextIsoDay = (day) => (Number(day) === 7 ? 1 : Number(day) + 1);
const prevIsoDay = (day) => (Number(day) === 1 ? 7 : Number(day) - 1);

const isOvernightHead = (shift) =>
  Number(shift.openingEnd) >= OVERNIGHT_HEAD_MIN_END;

const isOvernightTail = (shift) =>
  Number(shift.openingStart) === 0 &&
  Number(shift.openingEnd) > 0 &&
  Number(shift.openingEnd) <= OVERNIGHT_TAIL_MAX_END;

const shiftsForDay = (allShifts, day) =>
  orderBy(
    (allShifts || []).filter(
      (s) =>
        Number(s.day) === Number(day) &&
        (!s.noDineIn || !s.noPickup || !s.noDelivery)
    ),
    (s) => Number(s.openingStart),
    'asc'
  );

const startDisplay = (shift) =>
  Number(shift.openingStart) === 0 ? '12:00 AM' : shift.openingStartDisplay;

/**
 * Opening hours for one ISO weekday (1 = Monday ... 7 = Sunday), as the
 * segments a store actually trades, rather than the raw shift docs.
 *
 * Returns `[{ start, end, isNextDay }]`, empty when the store is closed.
 * `isNextDay` marks a segment whose closing time falls on the following day;
 * at most one segment per day can carry it, and because an overnight head has
 * to close at 11:59 PM it is always the last one.
 */
const getOpeningHourSegments = (day, allShifts) => {
  // List an overnight shift once, on the day it starts, borrowing the tail's
  // closing time. The day it bleeds into then has to drop its own midnight
  // segment, otherwise the same hours show up on both rows.
  const yesterdayRunsOvernight = shiftsForDay(allShifts, prevIsoDay(day)).some(
    isOvernightHead
  );
  const tomorrowsShifts = shiftsForDay(allShifts, nextIsoDay(day));

  return shiftsForDay(allShifts, day)
    .filter((s) => !(yesterdayRunsOvernight && isOvernightTail(s)))
    .map((s) => {
      const tail = isOvernightHead(s)
        ? tomorrowsShifts.find(isOvernightTail)
        : null;
      return {
        start: startDisplay(s),
        end: tail ? tail.openingEndDisplay : s.openingEndDisplay,
        isNextDay: Boolean(tail),
      };
    });
};

export default getOpeningHourSegments;
